import React from 'react';
import {
    Card,
    CardContent,
    Divider,
    Grid,
    IconButton,
    Tooltip,
} from '@material-ui/core';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { Heading } from '@klp/design/Heading';
import type { IKommentar } from '../services/rtkApi';
import { Typography } from '@klp/design/Typography';
import i18n from '@forvalter/i18n/config';
import { Edit } from '@material-ui/icons';
import DOMPurify from 'dompurify';
import { Block } from '@klp/design/Block';
import { Link } from '@klp/design/Link';
import {
    formatDateWithTime,
    parseSafeDate,
} from '@klp/utils/dateTimeFormatter';
import { isSameMinute } from 'date-fns';

export type FremhevdeKommentarerCardProps = {
    kommentar: IKommentar;
    handleRemoveHighlight: (id: string | undefined) => void;
    handleModal: (kommentar: IKommentar) => void;
};

const formatCardOwner = (kommentar: IKommentar) => {
    const createdDateObj = parseSafeDate(kommentar?.created);
    const hasNameAndDate = kommentar?.createdAvBruker?.navn && createdDateObj;
    const timeStamp = formatDateWithTime(createdDateObj);

    return hasNameAndDate
        ? `${kommentar?.createdAvBruker?.navn}, ${timeStamp}`
        : `${timeStamp}`;
};

const formatCardEditedByLocal = (kommentar: IKommentar) => {
    const name = kommentar?.oppdatertAvBruker?.navn;
    const timeStamp = formatDateWithTime(parseSafeDate(kommentar?.oppdatert));

    return name
        ? `${i18n.t('comments.edited')} ${name}, ${timeStamp}`
        : `${timeStamp}`;
};

const isCommentUpdated = (kommentar: IKommentar) => {
    const createdDateObj = parseSafeDate(kommentar?.created);
    const updatedDateObj = parseSafeDate(kommentar?.oppdatert);

    if (!createdDateObj || !updatedDateObj) {
        return false;
    }
    return isSameMinute(createdDateObj, updatedDateObj) === false;
};

export const FremhevdeKommentarerCard: React.FC<
    FremhevdeKommentarerCardProps
> = ({ kommentar, handleRemoveHighlight, handleModal }) => {
    return (
        <Card className="Card">
            <CardContent>
                <Grid container={true} className="CardHeader">
                    <Grid item={true}>
                        <Illustration icon={IconName.CIRCLE_INFO} />
                    </Grid>
                    <Grid item={true}>
                        <Heading klpHeadingLevel="SIX">
                            {kommentar.tittel}
                        </Heading>
                    </Grid>

                    <Grid item={true}>
                        <IconButton
                            color="inherit"
                            size="medium"
                            aria-label="Remove highlight"
                            onClick={(e) => {
                                handleRemoveHighlight(kommentar.id);
                                e.stopPropagation();
                            }}
                        >
                            <Illustration icon={IconName.CLOSE} size="xsmall" />
                        </IconButton>
                    </Grid>
                </Grid>
                <Divider />
                <Grid item={true} className="CardOwner">
                    <Typography
                        color="textSecondary"
                        klpTypographyVariant="small"
                    >
                        {formatCardOwner(kommentar)}
                        {isCommentUpdated(kommentar) && (
                            <span className="EditIcon" data-testid="EditIcon">
                                <Tooltip
                                    title={formatCardEditedByLocal(kommentar)}
                                    aria-label="edited-info"
                                >
                                    <Edit />
                                </Tooltip>
                            </span>
                        )}
                    </Typography>
                </Grid>
                <Grid container={true} className="CardContent">
                    <Grid item={true}>
                        <Typography klpTypographyVariant="small">
                            {kommentar?.sammendrag && (
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(
                                            kommentar?.sammendrag ?? '',
                                        ),
                                    }}
                                />
                            )}
                        </Typography>
                    </Grid>
                    {kommentar?.avkortetSammendrag && (
                        <Grid item={true} className="CardLink">
                            <Block paddingBottom="tiny">
                                <Link
                                    onClick={() => handleModal(kommentar)}
                                    klpTypographyVariant="small"
                                    translation="comments.readMore"
                                />{' '}
                            </Block>
                        </Grid>
                    )}
                </Grid>
            </CardContent>
        </Card>
    );
};
